
import React, { useContext, useRef } from 'react';
import { AppContext } from '../App.tsx';
import { AppStep } from '../types.ts';
import { ImagePlus, CloudUpload } from 'lucide-react';

const UploadPage: React.FC = () => {
  const ctx = useContext(AppContext);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        ctx?.dispatch({ type: 'SET_IMAGE', payload: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = () => {
    if (ctx?.state.imageData) {
      ctx.dispatch({ type: 'SET_STEP', payload: AppStep.EXTRACTION });
    }
  };

  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-4">Start your Transformation</h2>
        <p className="text-slate-400">Upload a hand-drawn sketch of your UI. Our 3-Engine pipeline will handle the rest.</p>
      </div>

      <div 
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative cursor-pointer group border-2 border-dashed rounded-3xl p-12 transition-all
          ${ctx?.state.imageData ? 'border-indigo-500 bg-indigo-500/5' : 'border-slate-800 hover:border-slate-600 bg-slate-900/50'}
        `}
      >
        <input 
          type="file" 
          ref={fileInputRef} 
          className="hidden" 
          accept="image/*" 
          onChange={handleFileChange} 
        />

        {ctx?.state.imageData ? (
          <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl">
            <img src={ctx.state.imageData} alt="Sketch Preview" className="w-full h-full object-contain" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <CloudUpload size={48} className="text-white animate-bounce" />
            </div>
          </div>
        ) : (
          <div className="py-12 flex flex-col items-center">
            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <ImagePlus size={32} className="text-slate-400 group-hover:text-indigo-400" />
            </div>
            <p className="text-slate-400 text-lg">Click or drag & drop to upload sketch</p>
            <p className="text-slate-600 text-sm mt-2">Supports PNG, JPG, WebP</p>
          </div>
        )}
      </div>

      <button
        disabled={!ctx?.state.imageData}
        onClick={handleNext}
        className={`
          mt-12 w-full py-4 rounded-2xl font-bold text-lg transition-all shadow-xl
          ${ctx?.state.imageData 
            ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/20' 
            : 'bg-slate-800 text-slate-500 cursor-not-allowed'}
        `}
      >
        Initialize Engine 1: Structural Extraction
      </button>
    </div>
  );
};

export default UploadPage;
